function toFiniteNumber (value) {
  if (value === undefined || value === null || value === '') {
    return null
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  const cleaned = `${value}`.trim().replace(/[^0-9+\-.]/g, '')
  if (!cleaned || ['+', '-', '.', '+.', '-.'].includes(cleaned)) {
    return null
  }
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeAngle (angle) {
  if (!Number.isFinite(angle)) {
    return null
  }
  const normalized = angle % 360
  return normalized < 0 ? normalized + 360 : normalized
}

function readPath (source = {}, path) {
  return String(path || '')
    .split('.')
    .reduce((value, key) => (value === undefined || value === null ? undefined : value[key]), source)
}

function resolveValueByPaths (source = {}, paths = []) {
  for (const path of paths) {
    const candidate = toFiniteNumber(readPath(source, path))
    if (candidate !== null) {
      return normalizeAngle(candidate)
    }
  }
  return null
}

function resolveAbsoluteEclipticLongitude (body = {}) {
  const directValue = resolveValueByPaths(body, [
    'geometry.absoluteLongitude',
    'absoluteEclipticLongitude',
    'absoluteEcliptic',
    'eclipticLongitude',
    'eclipticAbsoluteLongitude',
    'absoluteLongitude',
    'positionOnEcliptic'
  ])

  if (directValue !== null) {
    return directValue
  }

  const signIndex = toFiniteNumber(body.signIndex)
  if (signIndex === null) {
    return null
  }

  const signOffDeg = toFiniteNumber(body.signOffDeg) || 0
  const signOffMin = toFiniteNumber(body.signOffMin) || 0
  const signOffSec = toFiniteNumber(body.signOffSec) || 0

  return normalizeAngle(signIndex * 30 + signOffDeg + (signOffMin / 60) + (signOffSec / 3600))
}

function resolveSharedEclipticOverlayAngle (body = {}, referenceAsc = 0) {
  const absoluteLongitude = resolveAbsoluteEclipticLongitude(body)
  const normalizedAsc = normalizeAngle(toFiniteNumber(referenceAsc) || 0)
  if (absoluteLongitude === null || normalizedAsc === null) {
    return null
  }
  return normalizeAngle(absoluteLongitude - normalizedAsc)
}

function resolveChartAscAbsoluteLongitude (chartData = {}) {
  const directValue = resolveValueByPaths(chartData, [
    'specialPointsReal.ASC',
    'houses.0.cuspAbsoluteLongitude',
    'houses.0.geometry.absoluteLongitude'
  ])
  return directValue === null ? 0 : directValue
}

function resolveProjectedZuoshan (chartData = {}) {
  return resolveValueByPaths(chartData, [
    'zuoshanGeometry.relativeToAsc',
    'zuoshan'
  ])
}

function buildNormalizedHouses (houses = [], chartAscAbsoluteLongitude = 0) {
  const normalizedAsc = normalizeAngle(toFiniteNumber(chartAscAbsoluteLongitude) || 0)
  const normalizedHouses = (Array.isArray(houses) ? houses : []).map((house = {}, index) => {
    const cuspAbsoluteLongitude = resolveValueByPaths(house, [
      'cuspAbsoluteLongitude',
      'geometry.absoluteLongitude'
    ]) ?? normalizeAngle((toFiniteNumber(house.position) || 0) + normalizedAsc)

    const cuspRelativeToAsc = resolveValueByPaths(house, [
      'cuspRelativeToAsc',
      'geometry.relativeToAsc',
      'position'
    ]) ?? normalizeAngle(cuspAbsoluteLongitude - normalizedAsc)

    return {
      ...house,
      houseNumber: house.houseNumber || (index + 1),
      cuspAbsoluteLongitude,
      cuspRelativeToAsc,
      spanToNextCusp: null,
      geometry: {
        ...(house.geometry || {}),
        absoluteLongitude: cuspAbsoluteLongitude,
        relativeToAsc: cuspRelativeToAsc,
        houseNumber: house.houseNumber || (index + 1)
      }
    }
  })

  normalizedHouses.forEach((house, index) => {
    const nextHouse = normalizedHouses[(index + 1) % normalizedHouses.length]
    house.spanToNextCusp = normalizeAngle(nextHouse.cuspAbsoluteLongitude - house.cuspAbsoluteLongitude) ?? 0
  })

  return normalizedHouses
}

function resolveHouseNumberByLongitude (absoluteLongitude, houses = []) {
  const normalizedLongitude = normalizeAngle(absoluteLongitude)
  if (normalizedLongitude === null) {
    return 0
  }

  for (let index = 0; index < houses.length; index += 1) {
    const house = houses[index]
    const start = normalizeAngle(house.cuspAbsoluteLongitude)
    const span = normalizeAngle(house.spanToNextCusp)
    const offset = normalizeAngle(normalizedLongitude - start)
    if (offset === null || span === null) {
      continue
    }
    if (offset < span || (span === 0 && offset === 0)) {
      return house.houseNumber || (index + 1)
    }
  }

  return 0
}

function resolveDrawAngle ({
  absoluteLongitude,
  chartAscAbsoluteLongitude,
  referenceAscAbsoluteLongitude,
  houses = [],
  scaleAscAbsoluteLongitude,
  scaleHouses = [],
  equalHouse = false
}) {
  const normalizedAbsoluteLongitude = normalizeAngle(absoluteLongitude)
  const normalizedChartAsc = normalizeAngle(toFiniteNumber(chartAscAbsoluteLongitude) || 0)
  const normalizedReferenceAsc = normalizeAngle(
    toFiniteNumber(referenceAscAbsoluteLongitude) !== null
      ? toFiniteNumber(referenceAscAbsoluteLongitude)
      : normalizedChartAsc
  )

  if (normalizedAbsoluteLongitude === null || normalizedChartAsc === null || normalizedReferenceAsc === null) {
    return null
  }

  if (!equalHouse) {
    return normalizeAngle(normalizedAbsoluteLongitude - normalizedReferenceAsc)
  }

  const normalizedScaleAsc = normalizeAngle(
    toFiniteNumber(scaleAscAbsoluteLongitude) !== null
      ? toFiniteNumber(scaleAscAbsoluteLongitude)
      : normalizedChartAsc
  )
  const effectiveScaleHouses = Array.isArray(scaleHouses) && scaleHouses.length > 0
    ? scaleHouses
    : houses

  const houseNumber = resolveHouseNumberByLongitude(normalizedAbsoluteLongitude, effectiveScaleHouses)
  const houseIndex = effectiveScaleHouses.findIndex(house => (house.houseNumber || 0) === houseNumber)
  if (houseIndex < 0) {
    return normalizeAngle(normalizedAbsoluteLongitude - normalizedReferenceAsc)
  }

  const house = effectiveScaleHouses[houseIndex]
  const houseSpan = toFiniteNumber(house.spanToNextCusp) || 0
  const houseOffset = normalizeAngle(normalizedAbsoluteLongitude - house.cuspAbsoluteLongitude) || 0
  const scaledOffset = houseSpan > 0 ? (houseOffset / houseSpan) * 30 : 0
  const chartShift = normalizeAngle(normalizedScaleAsc - normalizedReferenceAsc) || 0
  return normalizeAngle(chartShift + houseIndex * 30 + scaledOffset)
}

function buildGeometry (body = {}, absoluteLongitude, relativeToAsc, houses = []) {
  const resolvedAbsoluteLongitude = normalizeAngle(absoluteLongitude)
  const resolvedRelativeToAsc = normalizeAngle(relativeToAsc)
  const houseNumber = body.houseNumber || readPath(body, 'geometry.houseNumber') || resolveHouseNumberByLongitude(resolvedAbsoluteLongitude, houses)
  return {
    ...(body.geometry || {}),
    absoluteLongitude: resolvedAbsoluteLongitude,
    relativeToAsc: resolvedRelativeToAsc,
    houseNumber
  }
}

function buildBodyDrawData (body = {}, options = {}) {
  const {
    absolutePaths = [],
    chartAscAbsoluteLongitude = 0,
    referenceAscAbsoluteLongitude = 0,
    houses = [],
    scaleAscAbsoluteLongitude,
    scaleHouses = [],
    equalHouse = false
  } = options

  const absoluteLongitude = resolveValueByPaths(body, absolutePaths)
  const relativeToAsc = resolveValueByPaths(body, [
    'geometry.relativeToAsc',
    'drawPos'
  ]) ?? normalizeAngle(absoluteLongitude - chartAscAbsoluteLongitude)
  const geometry = buildGeometry(body, absoluteLongitude, relativeToAsc, houses)
  const drawPos = resolveDrawAngle({
    absoluteLongitude: geometry.absoluteLongitude,
    chartAscAbsoluteLongitude,
    referenceAscAbsoluteLongitude,
    houses,
    scaleAscAbsoluteLongitude,
    scaleHouses,
    equalHouse
  })

  const prepared = {
    ...body,
    absoluteLongitude: geometry.absoluteLongitude,
    geometry,
    drawPos
  }

  return prepared
}

function buildChartDrawData (chartData = {}, options = {}) {
  const chartAscAbsoluteLongitude = resolveChartAscAbsoluteLongitude(chartData)
  const referenceAscAbsoluteLongitude = toFiniteNumber(options.referenceAscAbsoluteLongitude) !== null
    ? options.referenceAscAbsoluteLongitude
    : chartAscAbsoluteLongitude
  const equalHouse = Boolean(options.equalHouse)
  const normalizedHouses = buildNormalizedHouses(chartData.houses, chartAscAbsoluteLongitude)
  const scaleChartAscAbsoluteLongitude = toFiniteNumber(options.scaleChartAscAbsoluteLongitude) !== null
    ? options.scaleChartAscAbsoluteLongitude
    : chartAscAbsoluteLongitude
  const normalizedScaleHouses = Array.isArray(options.scaleHouses) && options.scaleHouses.length > 0
    ? buildNormalizedHouses(options.scaleHouses, scaleChartAscAbsoluteLongitude)
    : normalizedHouses
  const chartShift = normalizeAngle(chartAscAbsoluteLongitude - referenceAscAbsoluteLongitude) || 0

  const houses = normalizedHouses.map((house, index) => ({
    ...house,
    position: equalHouse
      ? normalizeAngle(chartShift + index * 30)
      : normalizeAngle(house.cuspAbsoluteLongitude - referenceAscAbsoluteLongitude)
  }))

  const specialPoints = Object.keys(chartData.specialPoints || {}).reduce((result, key) => {
    const numericValue = toFiniteNumber(chartData.specialPoints[key])
    result[key] = numericValue === null
      ? chartData.specialPoints[key]
      : normalizeAngle(numericValue + chartAscAbsoluteLongitude - referenceAscAbsoluteLongitude)
    return result
  }, {})

  const sharedDrawOptions = {
    chartAscAbsoluteLongitude,
    referenceAscAbsoluteLongitude,
    houses: normalizedHouses,
    scaleAscAbsoluteLongitude: scaleChartAscAbsoluteLongitude,
    scaleHouses: normalizedScaleHouses,
    equalHouse
  }

  return {
    ...chartData,
    houses,
    specialPoints,
    planets: (chartData.planets || []).map(body => buildBodyDrawData(body, {
      ...sharedDrawOptions,
      absolutePaths: ['geometry.absoluteLongitude', 'absoluteLongitude', 'positionOnEcliptic']
    })),
    logicPoints: (chartData.logicPoints || []).map(body => buildBodyDrawData(body, {
      ...sharedDrawOptions,
      absolutePaths: ['geometry.absoluteLongitude', 'absoluteLongitude', 'realPos']
    })),
    midpoints: (chartData.midpoints || []).map(body => buildBodyDrawData(body, {
      ...sharedDrawOptions,
      absolutePaths: ['geometry.absoluteLongitude', 'absoluteLongitude', 'position', 'positionOnEcliptic']
    })),
    starPositions: (chartData.starPositions || []).map(body => buildBodyDrawData(body, {
      ...sharedDrawOptions,
      absolutePaths: ['geometry.absoluteLongitude', 'absoluteLongitude', 'starRLng']
    })),
    xingxiuPositions: (chartData.xingxiuPositions || []).map(body => buildBodyDrawData(body, {
      ...sharedDrawOptions,
      absolutePaths: ['geometry.absoluteLongitude', 'absoluteLongitude', 'xingxiuRLng']
    })),
    tianyunPositions: (chartData.tianyunPositions || []).map(body => buildBodyDrawData(body, {
      ...sharedDrawOptions,
      absolutePaths: ['geometry.absoluteLongitude', 'absoluteLongitude', 'longitude']
    }))
  }
}

module.exports = {
  normalizeAngle,
  resolveAbsoluteEclipticLongitude,
  resolveSharedEclipticOverlayAngle,
  resolveChartAscAbsoluteLongitude,
  resolveProjectedZuoshan,
  resolveDrawAngle,
  buildChartDrawData
}
